// hooks/useCustomerAccount.ts
// Import Orval-generated clients
import { useGetTransactionList } from '@/shared/api/client/transactions';
import {
    useGetCustomerAccounts,
    useGetCustomerAccountsWithTransactions,
    useGetAllAccountBalances,
    useGetBalanceByProduct,
    useGetLoanEligibility,
    useGetStatement,
    useGetLoanLegerCard,
    useGetLoanCycles,
    useGetFullStatement, getFullStatement
} from '@/shared/api/client/customer-accounts';
import React from 'react';
import {AccountRequestDto, LoanStatementType} from "@/shared/api/model";

// Hook for getCustomerAccounts
export const useCustomerAccounts = (branch: string, custNo: string, product: string, online: boolean) => {
    const result = useGetCustomerAccounts(branch, custNo, { product, online }, {
        query: {
            queryKey: ['customerAccounts', branch, custNo, product, online]
        }
    });
    
    return [{
        isLoading: result.isPending,
        isError: result.isError,
        data: result.data,
        error: result.error
    }];
};

// Hook for getCustomerAccountsWithTransactions - only returns accounts with at least one transaction
export const useCustomerAccountsWithTransactions = (branch: string, custNo: string) => {
    const result = useGetCustomerAccountsWithTransactions(branch, custNo, {
        query: {
            queryKey: ['customerAccountsWithTransactions', branch, custNo]
        }
    });
    
    return [{
        isLoading: result.isPending,
        isError: result.isError,
        data: result.data,
        error: result.error
    }];
};

// Hook for getAllAccountBalances
export const useAllAccountBalances = (branch: string, custNo: string) => {
    const result = useGetAllAccountBalances(branch, custNo, {
        query: {
            queryKey: ['allAccountBalances', branch, custNo]
        }
    });
    
    return [{
        isLoading: result.isPending,
        isError: result.isError,
        data: result.data
    }];
};

// Hook for saving product = S, Shares product = H
export const useBalanceByProduct = (branch: string, custNo: string, product: string) => {
    const result = useGetBalanceByProduct(branch, custNo, product, {
        query: {
            queryKey: ['balances', branch, custNo, product]
        }
    });
    
    return [{
        isLoading: result.isPending,
        isError: result.isError,
        data: result.data
    }];
};

// Hook for getTransactionList
export const useTransactionList = (branch: string, custNo: string, glNo?: string) => {
    // Use the Orval-generated hook
    const result = useGetTransactionList(branch, custNo, glNo ? { glNo } : undefined, {
        query: {
            queryKey: ['transactions', branch, custNo, glNo || 'all']
        }
    });
    
    // Return the same structure that components expect
    return [{
        isLoading: result.isPending,
        isError: result.isError,
        data: result.data
    }];
};

// Hook for getLoanEligibility with optional product selection
export const useLoanEligibility = (branch: string, custNo: string, glNo?: string) => {
    const result = useGetLoanEligibility(branch, custNo, glNo ? { glNo } : undefined, {
        query: {
            queryKey: ['loanEligibility', branch, custNo, glNo || 'default'],
            enabled: !!branch && !!custNo
        }
    });
    
    return [{
        isLoading: result.isPending,
        isError: result.isError,
        data: result.data,
        refetch: result.refetch
    }, result.refetch] as const;
};

// Hook for getStatement
export const useStatement = (requestDto: AccountRequestDto, enabled: boolean = false) => {
    const branch = requestDto.branch || "";
    const custNo = requestDto.custNo || "";
    
    const result = useGetStatement(
        {
            mutation: {
                mutationKey: ['statement', custNo, branch, requestDto?.account || ""],
            }
        }
    );
    
    // Execute the mutation automatically when enabled is true (modal is opened)
    React.useEffect(() => {
        if (enabled && requestDto) {
            result.mutate({
                branch,
                custNo,
                data: requestDto
            });
        }
    }, [enabled, requestDto, branch, custNo, result.mutate]);
    
    // Return array pattern to maintain backward compatibility with components
    return [
        {
            isLoading: result.isPending,
            isError: result.isError,
            data: result.data,
            error: result.error,
            mutateAsync: async () => {
                const response = await result.mutateAsync({
                    branch,
                    custNo,
                    data: requestDto
                });
                return response;
            },
            execute: () => result.mutate({
                branch,
                custNo,
                data: requestDto
            })
        }
    ];
};

// Hook for getLoanLegerCard
export const useLegerCard = (requestDto: AccountRequestDto, enabled: boolean = false) => {
    console.log({requestDto});
    const branch = requestDto.branch || "";
    const custNo = requestDto.custNo || "";
    const glNo = requestDto.glNo || "";
    const loanStatementType = requestDto.loanStatementType || LoanStatementType.LEDGER_CARD;
    const cycle = requestDto.cycle;
    
    const result = useGetLoanLegerCard(branch, custNo, glNo, loanStatementType, { cycle }, {
        query: {
            queryKey: ['loanLedgerCard', branch, custNo, glNo, loanStatementType, cycle],
            enabled: enabled
        }
    });

    return [{
        isLoading: result.isPending,
        isError: result.isError,
        data: result.data,
        error: result.error
    }];
};

// Hook for getLoanCycles
export const useLoanCycles = (branch: string, custNo: string, glNo: string, enabled: boolean = true) => {
    const result = useGetLoanCycles(branch, custNo, glNo, {
        query: {
            queryKey: ['loanCycles', branch, custNo, glNo],
            enabled: enabled && !!branch && !!custNo && !!glNo
        }
    });
    
    return [{
        isLoading: result.isPending,
        isError: result.isError,
        data: result.data,
        error: result.error
    }];
};

// Hook for getFullStatement
export const useFullStatement = (custNo: string, branch: string, requestDto: AccountRequestDto) => {
    const result = useGetFullStatement();
    
    return {
        isLoading: result.isPending,
        isError: result.isError,
        data: result.data,
        mutateAsync: async () => {
            const response = await result.mutateAsync({
                branch,
                custNo,
                data: requestDto
            });
            return response.data;
        },
        mutate: () => {
            result.mutate({
                branch,
                custNo,
                data: requestDto
            });
        }
    };
};